import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { useMemo } from "react";
import { I18nUtil } from "@utils/i18n";
import { QUERY_KEYS } from "@config/constant";
import { IconMap } from "./useRecommendQuestion";
import { useOwnerPreference } from "./useOwnerPreference";
import { useOspApiClientProvider } from "./useOspApiClientProvider";

export const useChatRecommendQuestion = () => {
  const params = useParams<{ locale: string }>();
  const paramsLocale = (params.locale as string) || "en";
  const language = I18nUtil.getBackgroundLanguageKey(paramsLocale);
  const scope = "ONBOARDING_CHAT_REC";
  const { openDataApi } = useOspApiClientProvider();
  const { ownerPreference, isLoading } = useOwnerPreference();

  const {
    investment_goals = [],
    follow_themes = [],
    insight_style = "",
  } = ownerPreference || {};

  const relatedConditionQuery = useMemo(() => {
    const query: Record<string, string | string[]> = {};

    if (investment_goals.length) {
      query["related_condition[c1]"] = investment_goals;
    }

    if (follow_themes.length) {
      query["related_condition[c2]"] = follow_themes;
    }

    if (insight_style) {
      query["related_condition[c3]"] = insight_style;
    }

    return query;
  }, [follow_themes, insight_style, investment_goals]);

  const { data } = useQuery({
    queryKey: [QUERY_KEYS.RECOMMENDED_SCOPED_QUESTION, scope, ownerPreference],
    enabled: !!openDataApi?.recommend && !isLoading,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!openDataApi?.recommend) {
        throw new Error("OpenDataApi recommend is not available");
      }
      const res = await openDataApi.recommend.getScopedRecommend(scope, {
        offset: 0,
        limit: 20,
        ...relatedConditionQuery,
      } as any);
      return (
        res?.data?.data ?? {
          rows: [],
          total: 0,
          offset: 0,
          limit: 20,
        }
      );
    },
  });

  const questions = useMemo(() => {
    return (data?.rows || [])
      .map((item, index) => {
        const content =
          item.multi_language_contents?.find(
            (multiLangContent) => multiLangContent.language === language,
          ) || item.multi_language_contents?.[0];
        const prompt = item.multi_language_contents?.find(
          (multiLangContent) => multiLangContent.language === "en-US",
        )?.title;
        const displayKey = item.multi_language_contents?.find(
          (multiLangContent) => multiLangContent.language === "en-US",
        )?.display_title;
        const source_id = (item as any)?.source_id || "";
        const agent_id = (item as any)?.agent_id || "";

        return {
          id: item.id || "",
          question: content?.title || "",
          source_id,
          scope,
          title: content?.display_title,
          prompt,
          prompt_translation: content?.title,
          description: content?.description,
          icon: IconMap[index],
          language,
          key: displayKey,
          dataTrackingScope: `recommend-question-${content?.title}${source_id ? `-${source_id}` : ""}${agent_id ? `-${agent_id}` : ""}`,
        };
      })
      .filter((item) => item.question);
  }, [data?.rows, language]);

  return {
    questions,
  };
};
